
import React, { useEffect, useState } from 'react'
import { Row, Col, Alert, Badge, CloseButton } from 'react-bootstrap'
import Card from '../../../components/Card'

import { ProductSalesReport, buildProductSalesPdf } from '../../../features/productSales';
import { getProductSales } from '../../../services/api/productSales';
import { ApiError, ProductSalesByLocationMachine } from '../../../services/openapi';

import { useKeycloak } from '@react-keycloak/web'

import RefreshPull from '../../../components/RefreshPull';
import PdfExport from '../../../components/exports/PdfExport';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { useTranslation } from 'react-i18next';

// The report groups every row by location and machine and shows product totals,
// so it needs the full result set rather than one paged batch. Rows are
// fetched in batches of this size until the Content-Range total is reached.
const FETCH_BATCH_SIZE = 500;

// Wait until the user stops typing in a date before reloading the report.
const DATE_DELAY_MS = 800;

// yyyy-MM-dd in local time, the date input's format.
const formatInputDate = (date: Date) => {
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// The report starts a week back by default.
export const getDefaultStartDate = (today: Date = new Date()) =>
    formatInputDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7));

const ProductSalesView = () => {
    const [salesLines, setSalesLines] = useState<ProductSalesByLocationMachine[]>([]);
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const [siteFilter, setSiteFilter] = useLocalStorage<number[]>("sites", "siteFilter", []);
    const [siteFilterQuery, setSiteFilterQuery] = useLocalStorage<string>("sites", "siteFilterQuery", "");
    // yyyy-MM-dd from the date input; empty means no start date.
    const [startDate, setStartDate] = useState<string>(() => getDefaultStartDate());
    const [appliedStartDate, setAppliedStartDate] = useState<string>(startDate);
    // yyyy-MM-dd, included in the report; empty means up to now.
    const [endDate, setEndDate] = useState<string>(() => formatInputDate(new Date()));
    const [appliedEndDate, setAppliedEndDate] = useState<string>(endDate);
    const [errorMsg, setErrorMsg] = useState<string[]>([]);

    const { t } = useTranslation();
    const { keycloak } = useKeycloak();

    const loadAll = async () => {
        errorMsg.length = 0;
        document.querySelector('.errorMsg')?.classList.add("d-none");
        document.querySelector('#refresh')?.classList.remove("d-none");
        setIsRefreshing(true);

        const collected: ProductSalesByLocationMachine[] = [];

        try {
            let start = 0;
            let total = Number.POSITIVE_INFINITY;

            while (start < total) {
                const rangeString = `${start}-${start + FETCH_BATCH_SIZE}`;

                const result = await getProductSales(
                    keycloak.token || '',
                    rangeString,
                    { sites: siteFilter.join(), sitesQuery: siteFilterQuery, startDate: appliedStartDate, endDate: appliedEndDate }
                );

                const batch: ProductSalesByLocationMachine[] = result.body?.[0] ?? [];
                collected.push(...batch);

                const headerTotal = parseInt(result.header?.split('/')[1] ?? '', 10);
                total = Number.isNaN(headerTotal) ? collected.length : headerTotal;

                if (batch.length === 0) {
                    break;
                }

                start += FETCH_BATCH_SIZE;
            }

            setSalesLines(collected);
        } catch (error) {
            try {
                const apiError = error as ApiError;
                document.querySelector('.errorMsg')?.classList.remove("d-none");
                Object.entries<Record<string, any>>(JSON.parse(apiError.body)["errors"]).forEach((k) => { k[1].forEach((msg: string) => { errorMsg.push(msg); setErrorMsg([...errorMsg]); }) });
            } catch (ex) {
                errorMsg.push("Error handling response");
                setErrorMsg([...errorMsg]);
            }
        } finally {
            document.querySelector('#refresh')?.classList.add("d-none");
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteFilter, siteFilterQuery, appliedStartDate, appliedEndDate]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setAppliedStartDate(startDate);
            setAppliedEndDate(endDate);
        }, DATE_DELAY_MS);
        return () => clearTimeout(timer);
    }, [startDate, endDate]);

    // No dates after today, and the start date not after the end date. min/max only block dates
    // in the picker, so a typed one outside them is ignored too.
    const today = formatInputDate(new Date());
    const maxStartDate = endDate || today;
    const handleStartDateChange = (value: string) => {
        if (value <= maxStartDate) {
            setStartDate(value);
        }
    };
    const handleEndDateChange = (value: string) => {
        if (value === '' || (value <= today && value >= startDate)) {
            setEndDate(value);
        }
    };

    const handleGeneratePdf = () => buildProductSalesPdf(salesLines, appliedStartDate, appliedEndDate, {
        title: t('productSales.title'),
        startDate: t('productSales.startDate'),
        endDate: t('productSales.endDate'),
        generatedOn: t('productSales.generatedOn'),
        product: t('productSales.product'),
        quantity: t('productSales.quantity'),
        amount: t('productSales.amount'),
        subtotal: t('productSales.subtotal'),
        total: t('productSales.total'),
        totalsPerProduct: t('productSales.totalsPerProduct'),
        grandTotal: t('productSales.grandTotal'),
        site: t('productSales.site'),
        unknown: t('productSales.unknown')
    });

    return (
        <>
            <Row>
                <Col>
                    <Card className="rounded">
                        <Card.Body>
                            <Row className="d-none d-md-block">
                                <Col>&nbsp;</Col>
                            </Row>
                            <Row className="d-none d-md-block">
                                <Col>&nbsp;</Col>
                            </Row>
                            <Row>
                                <Col sm="12">
                                    <h4 className="mb-2 qvend-title">{t("productSales.title")}</h4>
                                </Col>
                            </Row>
                            <Row style={{ fontSize: '11px' }}>
                                <Col md={3} xs={5} style={{ paddingRight: 0 }}>
                                    <input
                                        type="date"
                                        className="form-control search"
                                        aria-label={t('productSales.startDate')}
                                        title={t('productSales.startDate')}
                                        value={startDate}
                                        max={maxStartDate}
                                        onChange={(e) => handleStartDateChange(e.target.value)}
                                    />
                                </Col>
                                <Col md={3} xs={5} style={{ paddingRight: 0 }}>
                                    <input
                                        type="date"
                                        className="form-control search"
                                        aria-label={t('productSales.endDate')}
                                        title={t('productSales.endDate')}
                                        value={endDate}
                                        min={startDate || undefined}
                                        max={today}
                                        onChange={(e) => handleEndDateChange(e.target.value)}
                                    />
                                </Col>
                                <Col>
                                    {((siteFilter && siteFilter.length > 0) || (siteFilterQuery && siteFilterQuery.length > 2)) && (
                                        <Badge pill bg="dark" className="p-1" onClick={() => { setSiteFilter([]); setSiteFilterQuery(""); window.localStorage.removeItem("searchDataSites") }}>
                                            <CloseButton variant="white" /> Sites
                                        </Badge>
                                    )}
                                </Col>
                                <Col className="text-end">
                                    <PdfExport
                                        onGenerate={handleGeneratePdf}
                                        filename="verkoop-per-locatie.pdf"
                                        disabled={salesLines.length === 0}
                                        className="btn btn-qvend"
                                        style={{ fontSize: '11px' }}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col sm="12" className="mt-2">
                                    <RefreshPull isRefreshing={isRefreshing} onSetPage={loadAll} />
                                    <Row className="d-none errorMsg">
                                        <Col>
                                            {
                                                errorMsg.map((item, index) => {
                                                    return <Alert key={index} variant="warning" className="alert" role="alert">
                                                        <span>{item}</span>
                                                    </Alert>;
                                                })
                                            }
                                        </Col>
                                    </Row>
                                    <ProductSalesReport rows={salesLines} />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    )
}

export default ProductSalesView
